'use client';

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { prefetchProfileModalBundle } from '@/lib/prefetch-profile-modal';

type ProfileModalContextValue = {
  openProfile: (address: string) => void;
  closeProfile: () => void;
  /** Address of the profile the modal is currently displaying (`null` when closed). */
  profileAddress: string | null;
};

const ProfileModalContext = createContext<ProfileModalContextValue | null>(null);

export function ProfileModalProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [profileAddress, setProfileAddress] = useState<string | null>(null);

  const openProfile = useCallback(
    (address: string) => {
      if (!address?.startsWith('0x')) return;
      void prefetchProfileModalBundle(queryClient, address);
      setProfileAddress(address);
    },
    [queryClient],
  );

  const closeProfile = useCallback(() => {
    setProfileAddress(null);
  }, []);

  const value = useMemo(
    () => ({ openProfile, closeProfile, profileAddress }),
    [openProfile, closeProfile, profileAddress],
  );

  return (
    <ProfileModalContext.Provider value={value}>{children}</ProfileModalContext.Provider>
  );
}

export function useProfileModal(): ProfileModalContextValue {
  const ctx = useContext(ProfileModalContext);
  if (!ctx) {
    throw new Error('useProfileModal must be used within ProfileModalProvider');
  }
  return ctx;
}

export function useProfileModalOptional(): ProfileModalContextValue | null {
  return useContext(ProfileModalContext);
}
